import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';

// ---------------------------------------------------------------------------
// SkillChip – inline chip showing a skill name with optional proficiency bar
// ---------------------------------------------------------------------------

interface SkillChipSignature {
  Args: {
    /** Skill name to display */
    name: string;
    /** Proficiency level (0-100) */
    level?: number;
    /** Whether to show the level bar (default: true when level is provided) */
    showLevel?: boolean;
    /** Color intent */
    intent?: 'default' | 'primary' | 'success' | 'warning';
  };
  Element: HTMLDivElement;
}

export default class SkillChip extends Component<SkillChipSignature> {
  get showLevel() {
    return this.args.showLevel !== false && this.args.level !== undefined;
  }

  get intentClass() {
    switch (this.args.intent) {
      case 'primary':
        return 'bg-violet-100 text-violet-700';
      case 'success':
        return 'bg-green-100 text-green-700';
      case 'warning':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  get barClass() {
    if (this.args.level === undefined) return 'bg-gray-300';
    if (this.args.level >= 80) return 'bg-green-500';
    if (this.args.level >= 60) return 'bg-violet-500';
    return 'bg-amber-500';
  }

  get levelStyle() {
    if (this.args.level === undefined) return htmlSafe('width: 0%');
    return htmlSafe(`width: ${this.args.level}%`);
  }

  <template>
    <div
      class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 shadow-sm
        {{this.intentClass}}"
      ...attributes
    >
      <span class="font-medium">{{@name}}</span>
      {{#if this.showLevel}}
        <div class="w-16 h-2 bg-white/60 rounded-full overflow-hidden">
          <div class="h-full rounded-full {{this.barClass}}" style={{this.levelStyle}}></div>
        </div>
        <span class="text-sm">{{@level}}%</span>
      {{/if}}
    </div>
  </template>
}
